import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FeedbackSubmitRequest, PartnerFeedbackView, PartnerSlideView } from "@mfa-aff/shared";
import { feedbackMock, feedbackSubmitMock, slidesMock } from "../mocks/panel-content";
import { get, send } from "./client";
import { partnerPaths } from "./paths";

export const slidesKey = ["partner", "slides"] as const;
export const feedbackKey = ["partner", "feedback"] as const;

/** `GET /slides`: active home slides in order. Image URLs are `/uploads/slides/…` (see `assetUrl`). */
export const useSlides = () =>
    useQuery({
        queryKey: slidesKey,
        queryFn: () => get<PartnerSlideView[]>(partnerPaths.slides, slidesMock),
        // A banner is decoration: a failed load hides the slider instead of retrying or showing an error.
        retry: false,
        staleTime: 5 * 60_000,
    });

/** `GET /feedback`: the partner's own messages, newest first, with the team's replies. */
export const useFeedbackList = (enabled = true) =>
    useQuery({ queryKey: feedbackKey, queryFn: () => get<PartnerFeedbackView[]>(partnerPaths.feedback, feedbackMock), enabled });

/** `POST /feedback`. Errors (`field: "body"`, too many messages) are shown on the form. */
export const useFeedbackSubmit = () => {
    const qc = useQueryClient();
    return useMutation({
        meta: { silent: true },
        mutationFn: (body: FeedbackSubmitRequest) => send<PartnerFeedbackView>("POST", partnerPaths.feedback, body, () => feedbackSubmitMock(body)),
        onSuccess: (item) => {
            qc.setQueryData<PartnerFeedbackView[]>(feedbackKey, (list) => (list ? [item, ...list.filter((f) => f.id !== item.id)] : list));
            return qc.invalidateQueries({ queryKey: feedbackKey });
        },
    });
};
